import { queueTextbox, queueTextboxSequence } from "../ui/textboxUI.js";
import { revealGameHud } from "../ui/gameHud.js";

/**
 * One-shot ambient textboxes fired the first time the player enters a given
 * room. Keyed by room id — check the debug HUD (backtick) for a room's id
 * while standing in it. Value is either one entry or an array of entries
 * (played back-to-back, Pokemon-style). Fires once ever per room per page
 * load — for the every-launch boot narration, see queueBootNarration below.
 *
 * Story / alert lines pause the world by default. For future environmental
 * flavor that should not freeze gameplay, set `environmental: true` (or
 * `pausesGame: false`) on the entry.
 */
export const ROOM_NARRATION = {
    3: { tag: "SYS // LOG", text: "You hear faint buzzing and whirring up ahead.", variant: "system" },
    4: { tag: "SYS // LOG", text: "That was a tight fit.", variant: "system" },
    7: { tag: "SYS // LOG", text: "You don't feel like fighting here is a good idea.", variant: "system" },
};

/**
 * One-shot ambient textboxes fired the first time the player moves directly
 * from one specific room into another (keyed "fromId>toId"). Checked
 * alongside ROOM_NARRATION above — both can fire on the same entry if the
 * destination room also has its own entry text.
 */
export const ROOM_TRANSITION_NARRATION = {};

const shown = new Set();
const shownTransitions = new Set();
const shownTutorialBeats = new Set();

function playNarrationEntry(entry, channel) {
    if (Array.isArray(entry)) {
        queueTextboxSequence(entry.map((e) => ({ ...e, channel: e.channel || channel })));
    } else {
        queueTextbox({ ...entry, channel: entry.channel || channel });
    }
}

export function checkRoomNarration(roomId, prevRoomId) {
    if (roomId == null) return;

    if (prevRoomId != null) {
        const transitionKey = `${prevRoomId}>${roomId}`;
        const transitionEntry = ROOM_TRANSITION_NARRATION[transitionKey];
        if (transitionEntry && !shownTransitions.has(transitionKey)) {
            shownTransitions.add(transitionKey);
            playNarrationEntry(transitionEntry, `R${prevRoomId}_${roomId}`);
        }
    }

    const entry = ROOM_NARRATION[roomId];
    if (entry && !shown.has(roomId)) {
        shown.add(roomId);
        playNarrationEntry(entry, `R${roomId}`);
    }
}

/**
 * Fires once, the first time an alarm actually clears in room 3 (i.e. the
 * player has defeated every guard robot in that room and the door unlocks).
 * See alarmLockdown.js's unlockAlarmRoom, which calls this.
 */
const ALARM_CLEAR_TUTORIAL_ROOM_ID = 3;
let alarmClearNarrationShown = false;

export function checkAlarmClearNarration(roomId) {
    if (alarmClearNarrationShown) return;
    if (roomId !== ALARM_CLEAR_TUTORIAL_ROOM_ID) return;
    alarmClearNarrationShown = true;
    queueTextbox({
        tag: "SYS // SECURITY",
        channel: "ALARM_CLEAR",
        text: "Defeating all enemies in a room turns off the security.",
        variant: "system",
    });
}

/**
 * Ad-sign flashback beat: fires once, the first time the player reaches
 * tile column 80 (world x / tileWidth). A mundane ad-clutter gripe followed
 * by a flashback line hinting the sign resembles the player.
 */
const AD_SIGN_TILE_X = 94;
let adSignNarrationShown = false;

export function checkAdSignNarration(tileX) {
    if (adSignNarrationShown) return;
    if (tileX == null || tileX < AD_SIGN_TILE_X) return;
    adSignNarrationShown = true;
    queueTextboxSequence([
        { tag: "SYS // LOG", channel: "AD_SIGN", text: "These ads are annoying...", variant: "system" },
        { tag: "MEMORY // FLASHBACK", channel: "AD_SIGN", text: "Doesn't that sign kind of look like you?", variant: "flashback" },
    ]);
}

/**
 * "You feel disoriented..." sequence — plays every time the player boots up
 * (not a one-shot room memory), ~1s after spawn. Ends by nudging the player
 * to open their own storage — the RAM tutorial no longer auto-opens.
 */
const BOOT_NARRATION = [
    {
        tag: "SYS // BOOT",
        text: "You feel disoriented and confused.",
        variant: "system",
    },
    {
        tag: "SYS // BOOT",
        text: "Something's missing. You can feel it.",
        variant: "system",
    },
    {
        tag: "SYS // BOOT",
        text: "Check your memory.",
        variant: "system",
        onShow: revealGameHud,
    },
];

export function queueBootNarration() {
    queueTextboxSequence(BOOT_NARRATION.map((e) => ({ ...e, channel: e.channel || "BOOT" })));
}

/**
 * Fired once after the player opens Memory during the kernel-only tutorial.
 */
export function queueMemoryTutorialNarration() {
    queueTextbox({
        tag: "SYS // MEMORY",
        text: "Your core modules have been unloaded. Reload what you can.",
        variant: "system",
        channel: "BOOT",
    });
}

export function resetTutorialNarrationBeats() {
    shownTutorialBeats.clear();
}

function scheduleTutorialBeatOnce(key, delaySeconds, callback) {
    if (shownTutorialBeats.has(key)) return;
    shownTutorialBeats.add(key);
    wait(delaySeconds, callback);
}

/**
 * First alarm: human flees and drops a RAM stick — pause on the beat.
 */
export function queueRamDropNarration() {
    queueTextboxSequence([
        {
            tag: "SYS // ALERT",
            channel: "ALERT",
            text: "He's dropped a RAM stick.",
            variant: "system",
        },
        {
            tag: "SYS // ALERT",
            channel: "ALERT",
            text: "That robot looks scary. See if you've got anything to fight it.",
            variant: "system",
        },
        {
            tag: "SYS // ALERT",
            channel: "ALERT",
            text: "Dodge the robot and grab the stick.",
            variant: "system",
        },
    ]);
}

const RAM_DROP_NARRATION_BEAT = 0.55;

export function scheduleRamDropNarration() {
    scheduleTutorialBeatOnce("r2_ram_drop", RAM_DROP_NARRATION_BEAT, () => {
        queueRamDropNarration();
    });
}

/**
 * After collecting the first dropped RAM stick (Bite / Bark unlocked).
 */
export function queueRamPickupNarration() {
    queueTextbox({
        tag: "SYS // COMBAT",
        channel: "ALERT",
        text: "Use Bite (LMB) to damage it. Bark (RMB) attracts humans to your location.",
        variant: "system",
    });
}

const RAM_PICKUP_NARRATION_BEAT = 0.45;

export function scheduleRamPickupNarration() {
    scheduleTutorialBeatOnce("r2_ram_pickup", RAM_PICKUP_NARRATION_BEAT, () => {
        queueRamPickupNarration();
    });
}
